import { useRef, useState, type ChangeEvent } from "react";
import { AlertCircle, FileSpreadsheet, Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useBulkImportUpload,
  type BulkImportType,
  type BulkImportTypeDefinition,
  type CreateBulkImportBatchResult,
} from "@/features/bulk-imports";

interface BulkImportUploaderProps {
  types: readonly BulkImportTypeDefinition[];
  isLoadingTypes: boolean;
  onUploaded: (result: CreateBulkImportBatchResult) => void;
}

export function BulkImportUploader({
  types,
  isLoadingTypes,
  onUploaded,
}: BulkImportUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedType, setSelectedType] = useState<BulkImportType | "">("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const { upload, isUploading, error, reset } = useBulkImportUpload();

  const selectedTypeDefinition = types.find(
    (typeDefinition) => typeDefinition.type === selectedType,
  );

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLocalError(null);
    reset();
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedType || !selectedFile) {
      setLocalError("Selecciona un tipo y un archivo.");
      return;
    }

    try {
      const result = await upload(selectedType, selectedFile);
      onUploaded(result);
    } catch {
      // Hook owns the API error state.
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setLocalError(null);
    reset();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-lg">Carga guiada</CardTitle>
        <CardDescription>
          Selecciona un formato, sube una planilla y revisa el preview.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,320px)_1fr]">
          <div className="space-y-2">
            <Label>Tipo de importacion</Label>
            <Select
              value={selectedType}
              onValueChange={(value) => {
                setSelectedType(value as BulkImportType);
                reset();
                setLocalError(null);
              }}
              disabled={isLoadingTypes || isUploading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar tipo" />
              </SelectTrigger>
              <SelectContent>
                {types.map((typeDefinition) => (
                  <SelectItem
                    key={typeDefinition.type}
                    value={typeDefinition.type}
                  >
                    {typeDefinition.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Archivo</Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={handleFileChange}
                disabled={isUploading}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="justify-start"
              >
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Elegir archivo
              </Button>
              <Button
                type="button"
                onClick={handleUpload}
                disabled={!selectedType || !selectedFile || isUploading}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Procesando
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Subir y validar
                  </>
                )}
              </Button>
              {selectedFile && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={clearFile}
                  disabled={isUploading}
                >
                  Cambiar
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-md border bg-muted/30 p-3 text-sm">
          {selectedFile ? (
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <span className="min-w-0 truncate font-medium">
                {selectedFile.name}
              </span>
              <span className="text-muted-foreground">
                {formatFileSize(selectedFile.size)}
              </span>
            </div>
          ) : (
            <span className="text-muted-foreground">
              Archivos soportados: .xlsx, .xls OOXML y .csv.
            </span>
          )}
          {selectedTypeDefinition && (
            <div className="mt-2 flex flex-wrap gap-1.5 text-xs text-muted-foreground">
              <span>Hoja: {selectedTypeDefinition.mainSheetName}</span>
              <span>·</span>
              <span>
                {selectedTypeDefinition.acceptedExtensions.join(", ")}
              </span>
            </div>
          )}
        </div>

        {(localError || error) && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{localError ?? error?.message}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
